import React, { ChangeEvent, useEffect, useState } from "react";

import ColorPicker from "components/basic/color-picker/ColorPicker";
import { T } from "services/lang/LanguageService";
import {
  HighlightRange,
  IProductTemplateAttribute,
  Validation,
} from "components/dashboard/product-template-table/utils/interfaces";
import { Button } from "primereact/button";
import { Checkbox, CheckboxChangeParams } from "primereact/checkbox";
import { Dialog } from "primereact/dialog";
import { Divider } from "primereact/divider";
import { Dropdown, DropdownChangeParams } from "primereact/dropdown";
import { InputSwitch, InputSwitchChangeParams } from "primereact/inputswitch";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { CellType } from "utils/enums";
import DatetimeAndInstantTypes from "./type-fields/DatetimeAndInstantTypes";
import ForeignKeyType from "./type-fields/ForeignKeyType";
import LongAndDoubleTypes from "./type-fields/LongAndDoubleTypes";
import MultiValueType from "./type-fields/MultiValueType";
import StringType from "./type-fields/StringType";
import TimeSpanType from "./type-fields/TimeSpanType";
import TimeType from "./type-fields/TimeType";
import UserSignatureType from "./type-fields/UserSignatureType";

type ProductTemplateAttributeDialogProps = {
  attribute: IProductTemplateAttribute;
  attributes: IProductTemplateAttribute[];
  index: number;
  onAttributeChange: (newAttribute: IProductTemplateAttribute, index: number) => void;
};

const ProductTemplateAttributeDialog: React.FC<ProductTemplateAttributeDialogProps> = ({
  attribute,
  attributes,
  index,
  onAttributeChange,
}) => {
  const [attr, setAttr] = useState(attribute);
  const [dialog, setDialog] = useState(false);
  const [editTextColors, setEditTextColors] = useState(false);
  const [editBgColors, setEditBgColors] = useState(false);

  useEffect(() => {
    setAttr(attribute);
    setEditTextColors(!!attribute.highlightColor?.color);
    setEditBgColors(!!attribute.highlightColor?.bgColor);
  }, [attribute]);

  const cancelDialog = () => {
    setAttr(attribute);
    setDialog(false);
  };

  const saveDialog = () => {
    onAttributeChange(attr, index);
    setDialog(false);
  };

  const onFieldChange = (
    e: DropdownChangeParams | ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => setAttr(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const onEditColor = (e: CheckboxChangeParams, field: "color" | "bgColor") => {
    field === "color" ? setEditTextColors(e.checked) : setEditBgColors(e.checked);

    if (e.checked) {
      setAttr(prev => ({
        ...prev,
        highlightColor: {
          ...prev.highlightColor,
          [field]: field === "color" ? "#000000" : "#ffffff",
        },
      }));
    } else {
      const { highlightColor, ...restAttr } = attr;
      const { [field]: extracted, ...restHighlightColor } = highlightColor!;
      highlightColor?.[field]
        ? setAttr({
            ...restAttr,
            highlightColor: restHighlightColor,
          })
        : setAttr({ ...restAttr });
    }
  };

  const onColorChange = (e: ChangeEvent<HTMLInputElement>) =>
    setAttr(prev => ({
      ...prev,
      highlightColor: prev.highlightColor
        ? { ...prev.highlightColor, [e.target.name]: e.target.value }
        : { [e.target.name]: e.target.value },
    }));

  const dialogFooterContent = () => {
    return (
      <div>
        <Button
          label={T("word.cancel")}
          icon="pi pi-times"
          onClick={cancelDialog}
          className="p-button-sm p-button-danger"
        />
        <Button
          label={T("action.save")}
          icon="pi pi-check"
          onClick={saveDialog}
          className="p-button-sm p-button-success"
        />
      </div>
    );
  };

  return (
    <>
      <Button icon="pi pi-cog" className="p-button-sm" onClick={() => setDialog(true)} />
      <Dialog
        blockScroll
        visible={dialog}
        onHide={() => setDialog(false)}
        header={`"${attr.displayName || T("word.no-name")}" ${T("word.column-configuration")}`}
        style={{ width: "min(100%, 1000px)" }}
        footer={dialogFooterContent}
      >
        <div className="card-class">
          <div className="p-fluid p-formgrid p-grid">
            <div className="p-field p-col-4">
              <label>{T("word.type")}</label>
              <Dropdown
                options={Object.keys(CellType)}
                value={attr.type ?? CellType.STRING}
                onChange={onFieldChange}
                name="type"
                className="p-inputtext-sm"
              />
            </div>
            <div className="p-field p-col-4">
              <label>{T("word.display-name")}</label>
              <InputText
                placeholder={T("word.display-name")}
                value={attr.displayName}
                onChange={onFieldChange}
                name="displayName"
                className="p-inputtext-sm"
                required
              />
            </div>
            <div className="p-field p-col-4">
              <label>{T("word.reference-name")}</label>
              <InputText
                placeholder={T("word.reference-name")}
                value={attr.referenceName}
                onChange={onFieldChange}
                name="referenceName"
                className="p-inputtext-sm"
                required
              />
            </div>
            <div className="p-field p-col-12">
              <label>
                {T("word.description")} ({T("word.optional")})
              </label>
              <InputTextarea
                placeholder={T("word.description")}
                value={attr.description}
                onChange={onFieldChange}
                name="description"
                className="p-inputtext-sm"
                style={{ resize: "vertical", minHeight: "4.4em" }}
              />
            </div>

            <Divider />

            <div className="flex w-full gap-3 m-3 h-2rem">
              <div className="flex-1 flex">
                <div className="flex align-items-center gap-3 px-3">
                  <label>{T("action.set-custom-text-color")}:</label>
                  <Checkbox
                    checked={editTextColors}
                    onChange={e => onEditColor(e, "color")}
                    className="p-inputtext-sm mb-2"
                  />
                </div>
                {editTextColors && (
                  <div className="flex align-items-center gap-3 px-3">
                    <label>{T("word.text-color")}:</label>
                    <ColorPicker
                      value={attr.highlightColor?.color}
                      onValueChange={onColorChange}
                      name="color"
                    />
                  </div>
                )}
              </div>

              <div className="flex-1 flex">
                <div className="flex align-items-center gap-3 px-3">
                  <label>{T("action.set-custom-background-color")}:</label>
                  <Checkbox
                    checked={editBgColors}
                    onChange={e => onEditColor(e, "bgColor")}
                    className="p-inputtext-sm mb-2"
                  />
                </div>
                {editBgColors && (
                  <div className="flex align-items-center gap-3 px-3">
                    <label>{T("word.background-color")}:</label>
                    <ColorPicker
                      value={attr.highlightColor?.bgColor}
                      onValueChange={onColorChange}
                      name="bgColor"
                    />
                  </div>
                )}
              </div>
            </div>

            <Divider />
            <div className="p-field p-col-6">
              <label>{T("word.required-permission")}</label>
              <InputText
                placeholder={T("message.required-permission-to-edit-this-column")}
                value={attr.requiredPermission}
                onChange={onFieldChange}
                name="requiredPermission"
                className="p-inputtext-sm"
              />
            </div>
            <div className="p-col-6 flex align-self-center justify-content-center gap-3">
              <label>{T("word.locked")}</label>
              <InputSwitch
                checked={attr.locked ?? false}
                onChange={onFieldChange}
                name="locked"
                className="p-inputswitch-sm"
              />
            </div>

            <Divider />
            <OptionalFields
              attribute={attr}
              attributes={attributes}
              type={attr.type}
              onAttributeChange={setAttr}
            />
          </div>
        </div>
      </Dialog>
    </>
  );
};

type OptionalFieldsProps = {
  attribute: IProductTemplateAttribute;
  attributes: IProductTemplateAttribute[];
  type: CellType;
  onAttributeChange: React.Dispatch<React.SetStateAction<IProductTemplateAttribute>>;
};
const OptionalFields: React.FC<OptionalFieldsProps> = ({
  attribute,
  attributes,
  type,
  onAttributeChange,
}) => {
  const onFieldChange = (e: ChangeEvent<HTMLInputElement> | InputSwitchChangeParams) =>
    onAttributeChange(prev => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));

  const handleBgHighlightsChange = (bgList: HighlightRange[]) => {
    onAttributeChange(prev => ({
      ...prev,
      bgHighlightRanges: bgList,
    }));
  };

  const handleValidationChange = (validators: Validation[]) => {
    onAttributeChange(prev => ({
      ...prev,
      validationOptions: {
        ...prev.validationOptions,
        validators,
      },
    }));
  };

  const handleRequiredFieldChange = (reqFields: string[]) => {
    onAttributeChange(prev => ({
      ...prev,
      validationOptions: {
        ...prev.validationOptions,
        requiredAttributes: reqFields,
      },
    }));
  };

  switch (type) {
    case CellType.LONG:
    case CellType.DOUBLE:
      return (
        <LongAndDoubleTypes attribute={attribute} onAttrChangeInLongAndDouble={onAttributeChange} />
      );
    case CellType.STRING:
      return <StringType attribute={attribute} onAttrChangeInString={onAttributeChange} />;
    case CellType.MULTI_VALUE:
      return <MultiValueType attribute={attribute} onAttributeChange={onAttributeChange} />;
    case CellType.DATETIME:
    case CellType.INSTANT:
      return (
        <DatetimeAndInstantTypes
          attribute={attribute}
          onAttrChangeInDatetimeAndInstant={onAttributeChange}
        />
      );
    case CellType.MULTI_FOREIGN_KEY:
    case CellType.FOREIGN_KEY:
      return (
        <ForeignKeyType
          attribute={attribute}
          onChange={onFieldChange}
          onValidationChange={handleValidationChange}
        />
      );
    case CellType.TIME:
      return (
        <TimeType
          attribute={attribute}
          onChange={onFieldChange}
          onValidationChange={handleValidationChange}
        />
      );
    case CellType.TIMESPAN:
      return (
        <TimeSpanType
          attribute={attribute}
          onBgHighlightsChange={handleBgHighlightsChange}
          onValidationChange={handleValidationChange}
        />
      );
    case CellType.USER_SIGNATURE:
      return (
        <UserSignatureType
          attribute={attribute}
          attributes={attributes}
          onRequiredFieldChange={handleRequiredFieldChange}
        />
      );
    default:
      return null;
  }
};

export default ProductTemplateAttributeDialog;
